import { useQuery, type QueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/hooks/queries';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook to fetch alt text from image_seo_metadata table
 * @param {string} imageUrl - The image URL to fetch alt text for
 * @param {string} fallbackAlt - Fallback alt text if none is found
 * @returns {string} The alt text from metadata or fallback
 */
export const useImageAltText = (imageUrl: string, fallbackAlt = ''): string => {
  const { data: altText = fallbackAlt } = useQuery({
    queryKey: queryKeys.imageSeo.altText(imageUrl),
    queryFn: async () => {
      if (!imageUrl || imageUrl.includes('placeholder')) {
        return fallbackAlt;
      }

      const { data, error } = await supabase
        .from('image_seo_metadata')
        .select('alt_text')
        .eq('image_url', imageUrl)
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        return fallbackAlt;
      }

      return data?.alt_text ?? fallbackAlt;
    },
    enabled: !!imageUrl && !imageUrl.includes('placeholder'),
    staleTime: 1000 * 60 * 60, // 1 hour - alt text doesn't change often
  });

  return altText;
};

/**
 * Batch fetch alt texts for multiple images
 * Uses React Query's prefetch to cache results
 * @param imageUrls - Array of image URLs
 * @param queryClient - React Query client instance
 * @returns Promise<Map<string, string>> - Map of imageUrl -> altText
 */
const filterValidUrls = (imageUrls: readonly string[]): string[] => {
  return imageUrls.filter(url => url && !url.includes('placeholder'));
};

const prefetchAltTexts = (
  data: Array<{ image_url: string; alt_text: string }>,
  queryClient: QueryClient
): void => {
  for (const item of data) {
    if (item.alt_text) {
      queryClient.setQueryData(queryKeys.imageSeo.altText(item.image_url), item.alt_text);
    }
  }
};

const collectCachedResults = (urls: string[], queryClient: QueryClient): Map<string, string> => {
  const results = new Map<string, string>();
  for (const url of urls) {
    const cached = queryClient.getQueryData(queryKeys.imageSeo.altText(url));
    if (cached) {
      results.set(url, cached as string);
    }
  }
  return results;
};

export const batchFetchAltTexts = async (
  imageUrls: readonly string[],
  queryClient: QueryClient
): Promise<Map<string, string>> => {
  if (!imageUrls.length) {
    return new Map();
  }

  const urlsToFetch = filterValidUrls(imageUrls);
  if (urlsToFetch.length === 0) {
    return new Map();
  }

  try {
    const { data, error } = await supabase
      .from('image_seo_metadata')
      .select('image_url, alt_text')
      .in('image_url', urlsToFetch)
      .eq('is_active', true);

    if (!error) {
      prefetchAltTexts(data, queryClient);
    }

    return collectCachedResults(urlsToFetch, queryClient);
  } catch {
    return new Map();
  }
};
